import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { omit } from 'lodash';
import { Confirm, DataTable, Form, INTEGRATION_CONFIGS, IntegrationConfigType, SAVE_INTEGRATION_CONFIG, TrackableButton, TriStateOption, useAlert, useIntegrationConfigs, useLocale, useServers, } from 'pe-core-frontend';
import { useState } from 'react';
const FilenamePatternVars = {
    '{YYYYMMDD}': 'Current date',
    '{YYYYMMDD_HMS}': 'Current date and time',
    '{RUN_ID}': 'Run ID',
    '{NAME}': 'Config name',
};
const FilenamePatternTooltip = () => {
    const { t } = useLocale();
    return (_jsxs("div", { className: 'p-4', children: [_jsxs("div", { children: [t('Variables:'), _jsx("ul", { className: 'pl-4', children: Object.keys(FilenamePatternVars).map((key) => (_jsxs("li", { style: { whiteSpace: 'nowrap' }, children: [_jsx("b", { children: key }), " - ", t(FilenamePatternVars[key])] }, key))) })] }), _jsxs("div", { className: 'pt-2', children: [t('Examples:'), _jsxs("ul", { className: 'pl-4', children: [_jsx("li", { children: '{NAME}_{YYYYMMDD}' }), _jsxs("li", { children: [t('report'), '_{RUN_ID}_{YYYYMMDD_HMS}'] })] })] })] }));
};
export const IntegrationConfigs = ({ clientId, direction }) => {
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { items, loading, error, deleteIntegrationConfig } = useIntegrationConfigs({
        clientId,
        direction,
    });
    const [saveIntegrationConfig] = useMutation(SAVE_INTEGRATION_CONFIG, {
        refetchQueries: [INTEGRATION_CONFIGS],
    });
    const [selectedIntegrationConfig, setSelectedIntegrationConfig] = useState(null);
    const [configToDelete, setConfigToDelete] = useState(null);
    const servers = useServers({ clientId });
    const FIELDS = [
        {
            name: 'name',
            label: t('Config Name'),
            type: 'text',
        },
        {
            name: 'serverId',
            label: t('Server'),
            type: 'lookup',
            options: servers.items?.map(x => ({ label: x.name, value: String(x.id) })) ?? [],
            required: true,
        },
        {
            name: 'type',
            label: t('Type'),
            type: 'lookup',
            options: Object.keys(IntegrationConfigType).map(p => ({ label: p, value: p })),
            required: true,
        },
        {
            name: 'isActive',
            label: t('Active?'),
            type: 'boolean',
        },
    ];
    const SETTING_FIELDS = [
        {
            name: 'filenamePattern',
            label: t('Filename Pattern'),
            type: 'text',
            required: false,
            additionalInfo: FilenamePatternTooltip,
        },
        {
            name: 'folder',
            label: t('Folder'),
            type: 'text',
            required: true,
        },
        {
            name: 'archiveFolder',
            label: t('Archive Folder'),
            type: 'text',
            required: false,
        },
        {
            name: 'archiveFiles',
            label: t('Archive Files?'),
            type: 'lookup',
            options: [
                { label: t('Default'), value: TriStateOption.DEFAULT },
                { label: t('Yes'), value: TriStateOption.YES },
                { label: t('No'), value: TriStateOption.NO },
            ],
            required: true,
        },
    ];
    const handleValidate = (config) => {
        const errors = {};
        if (!config.name) {
            errors.name = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!config.type) {
            errors.type = t('{field} is required', { values: { field: t('Type') } });
        }
        if (!config.serverId) {
            errors.serverId = t('{field} is required', { values: { field: t('Server') } });
        }
        // validate settings
        if (!config.settings.folder) {
            errors.folder = t('{field} is required', { values: { field: t('Folder') } });
        }
        if (config.settings.filenamePattern) {
            const regex = /\{(.*?)\}/g;
            const matches = config.settings.filenamePattern.match(regex);
            const patternVars = Object.keys(FilenamePatternVars);
            if (matches && matches?.length > 0) {
                for (const key of matches) {
                    if (!patternVars.includes(key)) {
                        errors.filenamePattern = t('Variable {name} is not valid.', {
                            values: { name: key },
                        });
                        break;
                    }
                }
            }
        }
        return errors;
    };
    const handleSave = async (config) => {
        try {
            // set selected to sync up between draft and selected
            setSelectedIntegrationConfig(config);
            // save
            await saveIntegrationConfig({
                variables: {
                    input: {
                        ...omit(config, ['__typename', 'createdAt', 'updatedAt']),
                        isActive: config.isActive.toString() === 'true',
                        clientId,
                        serverId: Number(config.serverId),
                        settings: {
                            ...omit(config.settings, ['__typename']),
                        },
                    },
                },
            });
            showAlert(t('Integration config saved'));
            setSelectedIntegrationConfig(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            showAlert(e.message, 'error');
        }
    };
    const handleDelete = async () => {
        if (configToDelete) {
            try {
                await deleteIntegrationConfig({
                    variables: {
                        id: configToDelete.id,
                    },
                });
                showAlert(t('Integration config deleted'));
                setConfigToDelete(null);
            }
            catch (e) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                showAlert(e.message, 'error');
            }
        }
    };
    return (_jsxs("div", { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx("div", { className: 'mb-4 flex flexJustify--end', children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Integration', tag: 'integrations_list', variant: 'contained', className: 'button button--primary margin--bottom_16', onClick: () => setSelectedIntegrationConfig({
                        id: 0,
                        clientId,
                        name: '',
                        direction,
                        isActive: true,
                        type: IntegrationConfigType.SFTP,
                        serverId: 0,
                        settings: {
                            filenamePattern: '',
                            folder: '',
                            archiveFolder: '',
                            archiveFiles: TriStateOption.DEFAULT,
                        },
                    }), children: t('Add Integration') }) }), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, loading: loading, rows: items || [], hideFooter: true, columns: [
                    {
                        field: 'name',
                        headerName: t('Config Name'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'type',
                        headerName: t('Type'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'direction',
                        headerName: t('Direction'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'isActive',
                        headerName: t('Active?'),
                        valueGetter: e => (e.row.isActive ? 'Yes' : 'No'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'action',
                        headerName: t('Actions'),
                        renderCell: ({ row }) => (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `integration_config_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedIntegrationConfig(row), className: 'margin--right_8' }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `integration_config_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => setConfigToDelete(row) })] })),
                        width: 100,
                        sortable: false,
                    },
                ] }), selectedIntegrationConfig && (_jsx(Form, { title: selectedIntegrationConfig.id ? 'Edit Integration' : 'Add Integration', modal: true, obj: {
                    ...selectedIntegrationConfig,
                    settings: omit(selectedIntegrationConfig.settings),
                }, fields: FIELDS, onValidate: handleValidate, onSave: handleSave, onCancel: () => setSelectedIntegrationConfig(null), children: ({ draft, setDraft, errors }) => (_jsx("div", { children: _jsx(Form, { obj: {
                            ...selectedIntegrationConfig.settings,
                        }, fields: SETTING_FIELDS, embedded: true, onChange: value => setDraft({ ...draft, settings: value }), errors: errors }) })) })), configToDelete && (_jsx(Confirm, { message: t("Are you sure you want to delete '{name}'?", {
                    values: { name: configToDelete.name },
                }), onConfirm: handleDelete, onCancel: () => setConfigToDelete(null) }))] }));
};
